import shp from 'shpjs'
import type { GeoPointFeature } from '../../../types'

/** Retorna "(xx.xx%)" da razão adicionados/total (ou 0 se total for 0). */
export function calcularPorcentagem(adicionados: number, total: number): string | number {
    if (total === 0) return 0
    const resultado = (adicionados / total) * 100
    return `(${resultado.toFixed(2)}%)`
}

/** Deixa o texto seguro para nome de arquivo: sem acento, sem caractere inválido. */
export function sanitizarNomeArquivo(texto: string): string {
    return String(texto ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
}

/**
 * Monta o nome do arquivo KML juntando as partes informadas (propriedade, talhão,
 * análise, profundidade...), já sanitizadas. Ignora partes vazias e cai no
 * fallback quando nada sobra.
 */
export function montarNomeArquivoKml(partes: (string | number | null | undefined)[], fallback: string): string {
    const limpas = partes
        .map((parte) => sanitizarNomeArquivo(String(parte ?? '')))
        .filter((parte) => parte !== '')

    return limpas.length > 0 ? limpas.join('_') : fallback
}

/** Centroide de uma geometria (média recursiva das coordenadas). */
function getCentroid(coords: any): { lat: number; lng: number } {
    let totalLat = 0
    let totalLng = 0
    let count = 0

    const processar = (arr: any) => {
        if (typeof arr[0] === 'number') {
            totalLng += arr[0]
            totalLat += arr[1]
            count++
        } else {
            arr.forEach(processar)
        }
    }
    processar(coords)

    return {
        lat: count > 0 ? totalLat / count : 0,
        lng: count > 0 ? totalLng / count : 0,
    }
}

export interface MalhaImportada {
    /** Pontos normalizados (INDICE/NOME/LAT/LONG) usados na UI. */
    malha: GeoPointFeature[]
    /** GeoJSON (FeatureCollection) enviado ao backend em malha_de_pontos. */
    malhaShp: {
        features: any[]
        fileName: string
        type: string
    }
    /** Quantidade total de pontos. */
    total: number
}

/**
 * Faz o parse de um shapefile (.shp/.zip) em malha de pontos.
 * Função pura: não toca em estado — retorna os dados ou lança em arquivo inválido.
 */
export async function parseShapefile(files: File[]): Promise<MalhaImportada | null> {
    if (!files || files.length === 0) return null

    const buffer = await files[0].arrayBuffer()
    const geojson: any = await shp(buffer)

    const features = geojson.features.map((data: any) => {
        if (data.properties.NOME === undefined && data.properties.AUTO_ID === undefined) {
            throw new Error('Arquivo Invalido')
        }
        data.color = '#ffffff'
        data.properties.NOME =
            parseInt(data.properties.NOME, 10) || parseInt(data.properties.AUTO_ID, 10)
        return data
    })

    const malha: GeoPointFeature[] = geojson.features.map((data: any) => {
        if (data.properties.NOME === undefined && data.properties.AUTO_ID === undefined) {
            throw new Error('Arquivo Invalido')
        }

        let lat: number
        let long: number
        if (data.geometry.type === 'Point') {
            ;[long, lat] = data.geometry.coordinates
        } else {
            const centro = getCentroid(data.geometry.coordinates)
            lat = centro.lat
            long = centro.lng
        }

        return {
            INDICE: data.properties.INDICE || data.properties.AUTO_ID,
            NOME: parseInt(data.properties.NOME, 10) || parseInt(data.properties.AUTO_ID, 10),
            LATITUDE: lat,
            LONGITUDE: long,
        }
    })

    return {
        malha,
        malhaShp: { features, fileName: 'POINT', type: 'FeatureCollection' },
        total: malha.length,
    }
}

/**
 * Converte a malha_de_pontos salva (GeoJSON) em pontos normalizados para a UI.
 * Usado ao EDITAR/COPIAR uma OS existente (mesma lógica do PreencherDados antigo).
 */
export function malhaSalvaParaPontos(malhaDePontos: any): GeoPointFeature[] {
    const features = malhaDePontos?.features ?? []
    return features.map((data: any) => {
        let lat: number
        let long: number
        if (data.geometry?.type === 'Point') {
            ;[long, lat] = data.geometry.coordinates
        } else {
            const centro = getCentroid(data.geometry.coordinates)
            lat = centro.lat
            long = centro.lng
        }
        return {
            INDICE: data.properties?.INDICE || data.properties?.AUTO_ID,
            NOME: parseInt(data.properties?.NOME, 10) || parseInt(data.properties?.AUTO_ID, 10),
            LATITUDE: lat,
            LONGITUDE: long,
        }
    })
}

/** Adiciona a cor da análise a cada ponto. */
function adicionarCorPontos(pontos: any[], cor: string) {
    return (pontos ?? []).map((p) => ({ ...p, COR: cor }))
}

/**
 * Converte as analises salvas de uma OS em cards (AnaliseAdicionada) para a lista.
 * Mesma transformação do PreencherDados antigo.
 */
export function analisesSalvasParaCards(analises: any[]): import('../../../types').AnaliseAdicionada[] {
    return (analises ?? []).map((item) => ({
        id: item?.id || String(item?.cod_analises?.[0]?.cod ?? ''),
        cod_analise: Number(item?.cod_analises?.[0]?.cod),
        pontos_selecionados: adicionarCorPontos(item?.pontos, item?.cod_analises?.[0]?.cor),
        profundidade: item?.profundidade,
        analise_nome: item?.cod_analises?.[0]?.nome,
        valor: item?.cod_analises?.[0]?.valor,
        cor: item?.cod_analises?.[0]?.cor,
        valor_analise: (
            Number(item?.cod_analises?.[0]?.valor) * (item?.pontos?.length ?? 0)
        ).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }),
    }))
}
